import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { products } from "../../data/products";
import "./PrivateSelectionEnhancer.css";

const WISHLIST_KEY = "playnice_wishlist";

const readWishlist = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(WISHLIST_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeMood = (mood) => String(mood || "").trim().toLowerCase();

const MOOD_LABELS = {
  clean: { sr: "Clean", en: "Clean" },
  summer: { sr: "Fresh", en: "Fresh" },
  date: { sr: "Date", en: "Date" },
  rich: { sr: "Rich", en: "Rich" },
  soft: { sr: "Soft", en: "Soft" },
  signature: { sr: "Signature", en: "Signature" }
};

const getLanguage = () => {
  try {
    return window.localStorage.getItem("playnice_lang") === "en" ? "en" : "sr";
  } catch {
    return "sr";
  }
};

const getMinPrice = (product) => {
  const prices = Object.values(product?.sizes || {}).map(Number).filter(Number.isFinite);
  return prices.length ? Math.min(...prices) : null;
};

const formatPrice = (value) => {
  if (!Number.isFinite(value)) return "";
  return Number.isInteger(value) ? `€${value}` : `€${value.toFixed(1)}`;
};

const getProfile = (selected, lang) => {
  const categoryCounts = new Map();
  const moodCounts = new Map();

  selected.forEach((product) => {
    if (product.category) {
      categoryCounts.set(product.category, (categoryCounts.get(product.category) || 0) + 1);
    }

    (product.moods || product.scentMoods || []).forEach((mood) => {
      const key = normalizeMood(mood);
      if (!key) return;
      moodCounts.set(key, (moodCounts.get(key) || 0) + 1);
    });
  });

  const categories = [...categoryCounts.entries()].sort((a, b) => b[1] - a[1]);
  const moods = [...moodCounts.entries()].sort((a, b) => b[1] - a[1]);

  const topCategory = categories[0]?.[0] || "";
  const topMoods = moods.slice(0, 2).map(([mood]) => MOOD_LABELS[mood]?.[lang] || mood);

  const categoryLabel = topCategory
    ? lang === "sr"
      ? `${topCategory} naginje`
      : `${topCategory} leaning`
    : lang === "sr"
      ? "Lični izbor"
      : "Personal selection";

  return {
    count: selected.length,
    categoryLabel,
    topMoods,
    moodKeys: moods.map(([mood]) => mood),
    categoryCounts
  };
};

const getRecommendations = (selected, wishlistIds, profile) => {
  if (!selected.length) return [];

  const directRecommendationCounts = new Map();

  selected.forEach((product) => {
    (product.recommendations || []).forEach((slug) => {
      if (!slug) return;
      directRecommendationCounts.set(slug, (directRecommendationCounts.get(slug) || 0) + 1);
    });
  });

  const selectedMoodSet = new Set(profile.moodKeys.slice(0, 4));
  const dominantCategory = [...profile.categoryCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];

  return products
    .filter((product) => !wishlistIds.includes(product.id))
    .map((product) => {
      const productMoods = (product.moods || product.scentMoods || []).map(normalizeMood);
      const moodOverlap = productMoods.filter((mood) => selectedMoodSet.has(mood));
      const directScore = (directRecommendationCounts.get(product.slug) || 0) * 10;
      const moodScore = moodOverlap.length * 3;
      const categoryScore = dominantCategory && product.category === dominantCategory ? 1 : 0;
      const ratingScore = Number(product.rating || 0) / 20;

      return {
        product,
        score: directScore + moodScore + categoryScore + ratingScore,
        direct: directScore > 0,
        sharedMoods: moodOverlap
      };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 2);
};

const getRecommendationReason = (direct, sharedMoods, lang) => {
  const labels = sharedMoods
    .slice(0, 2)
    .map((mood) => MOOD_LABELS[mood]?.[lang] || mood);

  if (direct && labels.length) {
    return lang === "sr"
      ? `Direktan pogodak za tvoj ${labels.join(" / ")} pravac.`
      : `A direct match for your ${labels.join(" / ")} side.`;
  }

  if (direct) {
    return lang === "sr"
      ? "Jaka veza sa parfemima koje već čuvaš."
      : "A strong link from fragrances you already keep.";
  }

  if (labels.length) {
    return lang === "sr"
      ? `Nastavlja tvoj ${labels.join(" / ")} pravac.`
      : `Extends your ${labels.join(" / ")} direction.`;
  }

  return lang === "sr"
    ? "Blizak karakter tvojoj selekciji."
    : "Close to the character of your selection.";
};

function PrivateSelectionEnhancer() {
  const [drawer, setDrawer] = useState(null);
  const [wishlistIds, setWishlistIds] = useState(() => readWishlist());
  const [lang, setLang] = useState(() => getLanguage());
  const [recommendationsOpen, setRecommendationsOpen] = useState(false);

  useEffect(() => {
    let frame = 0;

    const sync = () => {
      const nextDrawer = document.querySelector(".private-selection-drawer");
      setDrawer((current) => (current === nextDrawer ? current : nextDrawer));

      const nextWishlist = readWishlist();
      setWishlistIds((current) => {
        const unchanged =
          current.length === nextWishlist.length &&
          current.every((id, index) => id === nextWishlist[index]);
        return unchanged ? current : nextWishlist;
      });

      const nextLang = getLanguage();
      setLang((current) => (current === nextLang ? current : nextLang));
    };

    const scheduleSync = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        sync();
      });
    };

    sync();

    window.addEventListener("storage", scheduleSync);
    window.addEventListener("popstate", scheduleSync);
    window.addEventListener("playnice:locationchange", scheduleSync);
    document.addEventListener("click", scheduleSync, true);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("storage", scheduleSync);
      window.removeEventListener("popstate", scheduleSync);
      window.removeEventListener("playnice:locationchange", scheduleSync);
      document.removeEventListener("click", scheduleSync, true);
    };
  }, []);

  useEffect(() => {
    setRecommendationsOpen(false);
  }, [drawer, wishlistIds.length]);

  useEffect(() => {
    if (!drawer) return;

    const heading = drawer.querySelector(".private-selection-header h3");
    if (!heading) return;

    const baseTitle =
      lang === "sr" ? "Tvoja selekcija" : "Your Private Selection";

    const nextTitle =
      wishlistIds.length > 0
        ? `${baseTitle} · ${wishlistIds.length}`
        : baseTitle;

    if (heading.textContent !== nextTitle) {
      heading.textContent = nextTitle;
    }
  }, [drawer, lang, wishlistIds.length]);

  const selectedProducts = useMemo(
    () => products.filter((product) => wishlistIds.includes(product.id)),
    [wishlistIds]
  );

  const profile = useMemo(
    () => getProfile(selectedProducts, lang),
    [selectedProducts, lang]
  );

  const recommendations = useMemo(
    () => getRecommendations(selectedProducts, wishlistIds, profile),
    [selectedProducts, wishlistIds, profile]
  );

  if (!drawer || selectedProducts.length === 0) return null;

  const openRecommendation = (product) => {
    drawer.querySelector(".private-selection-header .close-button")?.click();

    window.setTimeout(() => {
      const nextUrl = `/product/${product.slug}`;
      window.history.pushState(
        {
          playniceProductModal: true,
          productSlug: product.slug,
          productOriginView: "shop"
        },
        "",
        nextUrl
      );
      window.dispatchEvent(new PopStateEvent("popstate"));
    }, 80);
  };

  return createPortal(
    <section className="private-selection-personal" aria-label={lang === "sr" ? "Tvoj mirisni profil" : "Your scent profile"}>
      <div className="private-selection-profile">
        <div className="private-selection-personal-head">
          <span>{lang === "sr" ? "TVOJ MIRISNI PROFIL" : "YOUR SCENT PROFILE"}</span>
          <small>{profile.count} {profile.count === 1 ? (lang === "sr" ? "parfem" : "fragrance") : (lang === "sr" ? "parfema" : "fragrances")}</small>
        </div>

        <div className="private-selection-profile-line">
          <strong>{profile.categoryLabel}</strong>
          {profile.topMoods.length > 0 && (
            <span>{profile.topMoods.join(" · ")}</span>
          )}
        </div>
      </div>

      {recommendations.length > 0 && (
        <div className={`private-selection-recommendations${recommendationsOpen ? " is-open" : ""}`}>
          <button
            type="button"
            className="private-selection-recommendations-toggle"
            aria-expanded={recommendationsOpen}
            onClick={() => setRecommendationsOpen((open) => !open)}
          >
            <span>{lang === "sr" ? "NA OSNOVU TVOG IZBORA" : "BASED ON YOUR SELECTION"}</span>
            <span className="private-selection-recommendations-toggle-meta">
              <small>{recommendations.length} {lang === "sr" ? "predloga" : "picks"}</small>
              <span className="private-selection-recommendations-chevron" aria-hidden="true">⌄</span>
            </span>
          </button>

          {recommendationsOpen && (
            <div className="private-selection-recommendation-list">
              {recommendations.map(({ product, direct, sharedMoods }) => {
                const minPrice = getMinPrice(product);
                const reason = getRecommendationReason(direct, sharedMoods, lang);

                return (
                  <button
                    key={product.id}
                    type="button"
                    className="private-selection-recommendation"
                    onClick={() => openRecommendation(product)}
                  >
                    <span className="private-selection-recommendation-media">
                      <img src={product.image || "/placeholder.png"} alt="" loading="lazy" />
                    </span>

                    <span className="private-selection-recommendation-copy">
                      <strong>{product.shortName || product.cardName || product.name}</strong>
                      <small>{reason}</small>
                    </span>

                    <span className="private-selection-recommendation-meta">
                      {minPrice !== null && <small>{lang === "sr" ? "od" : "from"} {formatPrice(minPrice)}</small>}
                      <span>{lang === "sr" ? "Pogledaj" : "View"} →</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>,
    drawer
  );
}

export default PrivateSelectionEnhancer;
